import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { PipelineStageStatus } from '@/types/domain/dashboard';
import { Filter, ChevronRight, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PipelineStatusCardProps {
  stages: PipelineStageStatus[];
  onStageClick?: (stageId: string) => void;
}

export const PipelineStatusCard: React.FC<PipelineStatusCardProps> = ({ stages, onStageClick }) => {
  const totalInPipeline = stages.reduce((acc, curr) => acc + curr.count, 0);

  return (
    <Card className="border-border rounded-2xl shadow-2xs h-full flex flex-col relative overflow-hidden">
      {/* Background Trading Grid Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <svg className="w-full h-full" viewBox="0 0 200 300" preserveAspectRatio="none" fill="none">
          <path d="M0 50 H200 M0 100 H200 M0 150 H200 M0 200 H200 M0 250 H200" stroke="#3d5ad6" strokeWidth="0.5" strokeDasharray="3 3" opacity="0.4" />
        </svg>
      </div>
      <CardHeader className="pb-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" />
            Procurement Pipeline Status
          </CardTitle>
          <span className="rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-bold">
            {totalInPipeline} Active
          </span>
        </div>
        <p className="text-[11px] text-foreground-secondary mt-0.5">
          Real-time bottleneck distribution across active workflow stages
        </p>
      </CardHeader>

      <CardContent className="pt-4 flex-1 flex flex-col justify-between space-y-3">
        <div className="space-y-2">
          {stages.map((stg) => {
            const isHighCount = stg.count > 30;
            const isMediumCount = stg.count > 10;

            return (
              <div
                key={stg.stageId}
                onClick={() => onStageClick && onStageClick(stg.stageId)}
                className={cn(
                  'flex items-center justify-between p-3 rounded-xl border border-border/70 bg-card hover:bg-muted/50 transition-all cursor-pointer group shadow-2xs',
                  isHighCount && 'border-amber-500/30 bg-amber-500/5'
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold',
                      isHighCount
                        ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                        : isMediumCount
                        ? 'bg-primary/15 text-primary'
                        : 'bg-muted text-foreground-secondary'
                    )}
                  >
                    {stg.count}
                  </div>
                  <span className="font-heading text-xs font-bold text-foreground group-hover:text-primary transition-colors">
                    {stg.stageName}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {isHighCount && (
                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Bottleneck
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-foreground-muted group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-border/60 pt-3 text-[11px] text-foreground-muted text-center font-medium">
          💡 Click any pipeline category to filter transactions
        </div>
      </CardContent>
    </Card>
  );
};
